import { apiGet, apiPost } from "app/services/apiServices";
import { AgTableClient } from "app/shared/AgTable";
import { useEffect, useRef, useState } from "react";
import { Button, Empty, message, Tag, Input } from "antd";
import { SearchOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import type { CellEditingStartedEvent, CellValueChangedEvent } from "ag-grid-community";
import {
  CheckCircleFilled,
  ExclamationCircleFilled,
} from "@ant-design/icons";
import Loader from "app/shared/Loader";
import _cloneDeep from "lodash/cloneDeep";
import moment from "moment";
import { API_ENDPOINT_FETCH_AUTO_SSR_LIST, API_ENDPOINT_UPDATE_AUTO_SSR } from "../../../../credentials.constants";
import BulkUploadWidget from "./BulkUploadWidget";

interface AutoSSRProps {
  onEditClick?: (rowData: any) => void;
}

export default function AutoSSR({ onEditClick }: AutoSSRProps) {
  const [tableData, setTableData] = useState<any[]>([]);
  const [originalData, setOriginalData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const gridRef = useRef<any>(null);
  const [gridApi, setGridApi] = useState<any>(null);
  const [globalSearch, setGlobalSearch] = useState("");
  const [isRowAdded, setIsRowAdded] = useState(false);

  const columnDefs = [
    {
      field: "customer",
      headerName: "Customer",
      editable: true,
      enableRowGroup: true,
      minWidth: 250,
      filter: "agTextColumnFilter",
    },
    {
      field: "forward_ssr_email",
      headerName: "SSR Email",
      // editable: true,
      enableRowGroup: true,
      minWidth: 280,
      filter: "agTextColumnFilter",
    },
    {
      field: "client_email",
      headerName: "Client email",
      editable: true,
      enableRowGroup: true,
      minWidth: 260,
      filter: "agTextColumnFilter",
    },
    {
      field: "sap_client_code",
      headerName: "SAP Client Code",
      editable: true,
      enableRowGroup: true,
      minWidth: 180,
      filter: "agTextColumnFilter",
      cellDataType: "text",
      valueParser: (params: any) => {
        // Always return as string to prevent number formatting
        return params.newValue != null ? String(params.newValue) : params.newValue;
      },
    },
    {
      field: "gst_number",
      headerName: "GST Number",
      editable: true,
      enableRowGroup: true,
      minWidth: 200,
      filter: "agTextColumnFilter",
    },
    {
      field: "gst_name",
      headerName: "GST Name",
      editable: true,
      enableRowGroup: true,
      minWidth: 300,
      filter: "agTextColumnFilter",
    },
    {
      field: "gst_state_name",
      headerName: "GST State",
      editable: true,
      enableRowGroup: true,
      minWidth: 180,
      filter: "agTextColumnFilter",
    },
    {
      field:"gst_state_code",
      headerName:"GST State Code",
      editable:true,
      enableRowGroup:true,
      minWidth: 250,
      filter:"agTextColumnFilter"   
    },
    {
      field:"gst_state_code",
      headerName:"GST State Code",
      editable:true,
      enableRowGroup:true,
      minWidth: 250,
      filter:"agTextColumnFilter"   
    },
    {
      field:"group_code",
      headerName:"Group Code",
      editable:true,
      enableRowGroup:true,
      minWidth: 250,
      filter:"agTextColumnFilter"   
    },
    {
      field: "created_at",
      headerName: "Created At",
      enableRowGroup: true,
      minWidth: 200,
      filter: "agDateColumnFilter",
      filterParams: {
        comparator: (filterLocalDateAtMidnight: any, cellValue: any) => {
          if (!cellValue) return -1;
          const cellDate = new Date(cellValue);
          const cellDateNoTime = new Date(
            cellDate.getFullYear(),
            cellDate.getMonth(),
            cellDate.getDate()
          );
          if (cellDateNoTime.getTime() === filterLocalDateAtMidnight.getTime()) {
            return 0;
          }
          return cellDateNoTime < filterLocalDateAtMidnight ? -1 : 1;
        },
      },
      valueFormatter: (params: any) => {
        if (!params.value) return "";
        return moment(params.value).format("DD-MM-YYYY");
      },
    },
    {
      field: "updated_at",
      headerName: "Updated At",
      enableRowGroup: true,
      minWidth: 200,
      filter: "agDateColumnFilter",
      filterParams: {
        comparator: (filterLocalDateAtMidnight: any, cellValue: any) => {
          if (!cellValue) return -1;
          const cellDate = new Date(cellValue);
          const cellDateNoTime = new Date(
            cellDate.getFullYear(),
            cellDate.getMonth(),
            cellDate.getDate()
          );
          if (cellDateNoTime.getTime() === filterLocalDateAtMidnight.getTime()) {
            return 0;
          }
          return cellDateNoTime < filterLocalDateAtMidnight ? -1 : 1;
        },
      },
      valueFormatter: (params: any) => {
        if (!params.value) return "";
        return moment(params.value).format("DD-MM-YYYY");
      },
    },
    {
      field: "action",
      headerName: "Action",
      width: 180,
      cellRenderer: (params: any) => {
        // Only show delete button for new rows
        if (params.data?.isNew) {
          return (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
              <DeleteOutlined
                style={{
                  fontSize: 18,
                  color: "#ff4d4f",
                  cursor: "pointer",
                }}
                onClick={() => handleDeleteNewRow(params.data, params.node)}
              />
            </div>
          );
        }
        return null;
      },
    },
  ];
  

  const fetchAutoSsrData = async () => {
    setIsLoading(true);
    try {
        const response = await apiGet(API_ENDPOINT_FETCH_AUTO_SSR_LIST);
    //   const response = await apiGet("http://localhost:7002/api/creds/email/autoSSR");
  
      
      if (response.status) {
        // Based on API response: { status: true, data: { credentials: [...] } }
        const data = response.data?.credentials || response.data?.data?.credentials || [];
      
        
        // Log first item to see actual field names
        if (Array.isArray(data) && data.length > 0) {
          console.log("First data item fields:", Object.keys(data[0]));
          console.log("First data item:", data[0]);
        }
        
        if (Array.isArray(data)) {
          // Ensure sap_client_code is always a string to prevent number formatting
          const processedData = data.map((item: any) => ({
            ...item,
            sap_client_code: item.sap_client_code ? String(item.sap_client_code) : item.sap_client_code,
          }));
          setTableData(processedData);
          setOriginalData(_cloneDeep(processedData));
        
        } else {
          
          messageApi.warning("Invalid data format received");
          setTableData([]);
          setOriginalData([]);
        }
      } else {
       
        messageApi.error("Failed to fetch Auto SSR data");
        setTableData([]);
        setOriginalData([]);
      }
    } catch (error) {
      
      messageApi.error("Error fetching Auto SSR data");
      setTableData([]);
      setOriginalData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddRow = () => {
    const newItem = {
      customer: "-",
      forward_ssr_email: "-",
      client_email: "-",
      sap_client_code: "-",
      gst_number: "-",
      gst_name: "-",
      gst_state_name: "-",
      from_email: "-",
      reply_to_email: "-",
      from_name: "-",
      isNew: true,
      tempId: `temp_${Date.now()}_${Math.random()}`, // Unique temporary ID for new rows
    };

    setTableData((prevState: any) => [newItem, ...prevState]);
    setOriginalData((prevState: any) => [newItem, ...prevState]);
    setIsRowAdded(true);
    
    // Show save button when a new row is added
    const saveBtn = document.getElementById("autoSsrSaveBtn");
    if (saveBtn) {
      saveBtn.style.display = "flex";
    }
  };

  const handleDeleteNewRow = (rowData: any, node?: any) => {
    // Only allow deletion of new rows (rows with isNew flag)
    if (!rowData?.isNew) {
      return;
    }

    const tempId = rowData.tempId;

    // Remove from tableData using tempId
    setTableData((prevData: any[]) => {
      return prevData.filter((row) => {
        if (row.isNew && tempId) {
          return row.tempId !== tempId;
        }
        return true;
      });
    });

    // Remove from originalData
    setOriginalData((prevData: any[]) => {
      return prevData.filter((row) => {
        if (row.isNew && tempId) {
          return row.tempId !== tempId;
        }
        return true;
      });
    });

    // Update grid using transaction
    if (gridApi && node) {
      gridApi.applyTransaction({ remove: [node.data] });
    } else if (gridApi && tempId) {
      // Fallback: find and remove the node by tempId
      gridApi.forEachNode((gridNode: any) => {
        if (gridNode.data?.isNew && gridNode.data?.tempId === tempId) {
          gridApi.applyTransaction({ remove: [gridNode.data] });
        }
      });
    }

    // Check if there are any new rows or changes left, if not hide save button
    setTimeout(() => {
      if (gridApi) {
        let hasNewRows = false;
        let hasChanges = false;
        
        gridApi.forEachNode((gridNode: any) => {
          if (!gridNode.data) return;
          
          if (gridNode.data.isNew) {
            hasNewRows = true;
          } else {
            const rowId = gridNode.data.id;
            if (rowId) {
              const original = originalData.find((item) => item.id === rowId);
              if (original) {
                const editableFields = [
                  "customer",
                  "forward_ssr_email",
                  "client_email",
                  "sap_client_code",
                  "gst_number",
                  "gst_name",
                  "gst_state_name",
                  "from_email",
                  "reply_to_email",
                  "from_name",
                ];
                editableFields.forEach((key) => {
                  const newValue = (gridNode.data[key] ?? "").toString().trim();
                  const oldValue = (original[key] ?? "").toString().trim();
                  if (newValue !== oldValue) {
                    hasChanges = true;
                  }
                });
              }
            }
          }
        });

        const saveBtn = document.getElementById("autoSsrSaveBtn");
        if (saveBtn && !hasNewRows && !hasChanges) {
          saveBtn.style.display = "none";
        }
      }
    }, 0);
  };

  const handleCellValueChanged = (event: CellValueChangedEvent) => {
    // Show save button only when a cell value is actually changed (after Enter is pressed)
    // This event fires when the cell editing is complete and value has changed
    const saveBtn = document.getElementById("autoSsrSaveBtn");
    if (saveBtn && event.newValue !== event.oldValue) {
      saveBtn.style.display = "flex";
    }

    // Auto-update forward_ssr_email when sap_client_code changes
    if (event.colDef?.field === "sap_client_code") {
      const node = event.node;
      if (node && node.data) {
        const sapClientCode = event.newValue ? String(event.newValue).trim() : "";
        const newEmail = sapClientCode ? `${sapClientCode}@air.finkraft.ai` : "";
        
        // Update the node data
        node.setDataValue("forward_ssr_email", newEmail);
        
        // Also update the local state
        setTableData((prevData) => {
          return prevData.map((row) => {
            // Match by id for existing rows, or by isNew flag and sap_client_code for new rows
            const isMatchingRow = 
              (row.id && node.data.id && row.id === node.data.id) ||
              (row.isNew && node.data.isNew && row.sap_client_code === String(event.oldValue || "").trim());
            
            if (isMatchingRow) {
              return {
                ...row,
                forward_ssr_email: newEmail,
                sap_client_code: sapClientCode || row.sap_client_code,
              };
            }
            return row;
          });
        });
      }
    }
  };

  const handleSaveClick = async () => {
    if (!gridApi) return;

    const editableFields = [
      "customer",
      "forward_ssr_email",
      "client_email",
      "sap_client_code",
      "gst_number",
      "gst_name",
      "gst_state_name",
      "from_email",
      "reply_to_email",
      "from_name",
    ];
    const modifiedRows: any[] = [];
    const newRows: any[] = [];
    const keyField = "id";

    // Find all modified and new rows
    gridApi.forEachNode((node: any) => {
      if (!node.data) return;

      const rowId = node.data[keyField];
      const isNew = node.data?.isNew === true;

      // Handle new rows
      if (isNew) {
        const newRowData: Record<string, any> = {};

        editableFields.forEach((key) => {
          const value = (node.data[key] ?? "").toString().trim();
          // Convert "-" to empty string for API
          newRowData[key] = value === "-" ? "" : value;
        });

        newRows.push(newRowData);
        return;
      }

      // Handle existing rows (only if they have an id)
      if (!rowId) return;

      const original = originalData.find((item) => item[keyField] === rowId);
      if (!original) return;

      const changes: Record<string, any> = {};
      let hasChanges = false;

      editableFields.forEach((key) => {
        const newValue = (node.data[key] ?? "").toString().trim();
        const oldValue = (original[key] ?? "").toString().trim();
        if (newValue !== oldValue) {
          changes[key] = newValue;
          hasChanges = true;
        }
      });

      if (hasChanges) {
        modifiedRows.push({
          id: rowId,
          ...changes,
        });
      }
    });

    // Validate new rows - check required fields
    const requiredFields = ["customer", "forward_ssr_email", "client_email", "sap_client_code"];
    const missingFieldsMap = new Map<number, string[]>();
    
    newRows.forEach((row, index) => {
      const missingFields: string[] = [];
      requiredFields.forEach((field) => {
        const value = (row[field] ?? "").toString().trim();
        if (!value || value === "-") {
          missingFields.push(field);
        }
      });
      if (missingFields.length > 0) {
        missingFieldsMap.set(index, missingFields);
      }
    });

    // If any new rows have missing required fields, show error and prevent save
    if (missingFieldsMap.size > 0) {
      const fieldLabels: Record<string, string> = {
        customer: "Customer",
        forward_ssr_email: "SSR Email",
        client_email: "Client Email",
        sap_client_code: "SAP Client Code",
      };
      
      const errorMessages: string[] = [];
      missingFieldsMap.forEach((missingFields, index) => {
        const fieldNames = missingFields.map((field) => fieldLabels[field] || field).join(", ");
        errorMessages.push(`${fieldNames}`);
      });
      
      const errorMessage = `Please fill all required fields before saving: ${errorMessages.join("; ")}`;
      messageApi.error(errorMessage, 5);
      return;
    }

    // Combine new rows and modified rows
    const allRowsToSave = [...newRows, ...modifiedRows];

    if (allRowsToSave.length === 0) {
      messageApi.info("No changes to save.");
      return;
    }

    setIsSaving(true);
    const verifiedRows: any[] = [];
    const failedRows: any[] = [];
    const savedRecordsMap = new Map(); // Map to track saved records: originalRow -> savedRecord

    // Save each row (new or modified)
    for (const row of allRowsToSave) {
      try {
        const response = await apiPost(API_ENDPOINT_UPDATE_AUTO_SSR, row);
        // const response = await apiPost("http://localhost:7002/api/creds/email/autoSSRUpdate", row);
        if (response.status) {
          verifiedRows.push(row);
          // Store the saved record from API response (includes ID for new rows)
          const savedRecord = response.data?.updatedCredential || response.data?.insertedCredential || response.data;
          if (savedRecord) {
            // Use row as key to match later
            savedRecordsMap.set(row, { ...row, ...savedRecord, isNew: false });
          } else {
            savedRecordsMap.set(row, { ...row, isNew: false });
          }
        } else {
          failedRows.push(row);
          const rowIdentifier = row.id ? `ID ${row.id}` : "new row";
          messageApi.error(`Failed to save ${rowIdentifier}`);
        }
      } catch (error) {
        failedRows.push(row);
        const rowIdentifier = row.id ? `ID ${row.id}` : "new row";
        messageApi.error(`Error saving ${rowIdentifier}`);
      }
    }

    setIsSaving(false);

    if (verifiedRows.length === 0) {
      messageApi.warning("No rows were saved successfully.");
      return;
    }

    if (failedRows.length > 0) {
      messageApi.warning(`Some rows failed to save.`);
    } else {
      messageApi.success(`Save success!`);
    }

    // Update local state without refetching
    if (savedRecordsMap.size > 0) {
      // Create a helper to match rows
      const findSavedRecord = (existingRow: any) => {
        const entries = Array.from(savedRecordsMap.entries());
        for (let i = 0; i < entries.length; i++) {
          const [originalRow, savedRecord] = entries[i];
          // For existing rows, match by ID
          if (existingRow.id && originalRow.id === existingRow.id) {
            return savedRecord;
          }
          // For new rows, match by isNew flag and compare editable fields
          if (existingRow.isNew && originalRow.isNew) {
            const fieldsMatch = editableFields.every((field) => {
              const existingValue = (existingRow[field] ?? "").toString().trim();
              const originalValue = (originalRow[field] ?? "").toString().trim();
              return existingValue === originalValue;
            });
            if (fieldsMatch) {
              return savedRecord;
            }
          }
        }
        return null;
      };

      // Update tableData
      setTableData((prevData) => {
        return prevData.map((existingRow) => {
          const savedRecord = findSavedRecord(existingRow);
          return savedRecord || existingRow;
        });
      });

      // Update originalData
      setOriginalData((prevData) => {
        const updated = prevData.map((existingRow) => {
          const savedRecord = findSavedRecord(existingRow);
          return savedRecord || existingRow;
        }).filter((row) => !row.isNew); // Remove isNew flag from originalData

        // Add any saved records that weren't in originalData (new rows)
        savedRecordsMap.forEach((savedRecord) => {
          if (!updated.find((row) => row.id === savedRecord.id)) {
            updated.push(savedRecord);
          }
        });

        return updated;
      });

      // Update grid
      if (gridApi) {
        gridApi.forEachNode((node: any) => {
          if (!node.data) return;
          const savedRecord = findSavedRecord(node.data);
          if (savedRecord) {
            node.setData(savedRecord);
          }
        });
      }
    }

    // Hide save button
    const saveBtn = document.getElementById("autoSsrSaveBtn");
    if (saveBtn) saveBtn.style.display = "none";
  };

  useEffect(() => {
    fetchAutoSsrData();
  }, []);

  useEffect(() => {
    if (gridRef.current) {
      setGridApi(gridRef.current.api);
    }
  }, [gridRef.current]);

  const handleGlobalSearch = (searchValue: string) => {
    const trimmed = searchValue.trim().toLowerCase();

    let filteredData: any[];
    
    if (!trimmed) {
      filteredData = _cloneDeep(originalData);
    } else {
      filteredData = originalData.filter((row) =>
        row?.customer?.toLowerCase().includes(trimmed) ||
        row?.forward_ssr_email?.toLowerCase().includes(trimmed) ||
        row?.client_email?.toLowerCase().includes(trimmed) ||
        row?.sap_client_code?.toLowerCase().includes(trimmed) ||
        row?.gst_number?.toLowerCase().includes(trimmed) ||
        row?.gst_name?.toLowerCase().includes(trimmed) ||
        row?.gst_state_name?.toLowerCase().includes(trimmed) ||
        row?.from_email?.toLowerCase().includes(trimmed) ||
        row?.reply_to_email?.toLowerCase().includes(trimmed) ||
        row?.from_name?.toLowerCase().includes(trimmed)
      );
    }

    setTableData(filteredData);
    
    // Explicitly update the grid via API to ensure it reflects the filtered data
    const currentGridApi = gridRef.current?.api || gridApi;
    if (currentGridApi) {
      currentGridApi.setRowData(filteredData);
    }
  };

  useEffect(() => {
    if (originalData.length > 0) {
      handleGlobalSearch(globalSearch);
    }
  }, [globalSearch, originalData]);

  // Sync grid when tableData changes (fallback mechanism)
  useEffect(() => {
    const currentGridApi = gridRef.current?.api || gridApi;
    if (currentGridApi && tableData) {
      currentGridApi.setRowData(tableData);
    }
  }, [tableData, gridApi]);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: "10px",
          marginBottom: "10px",
          paddingRight: "10px",
          paddingLeft: "10px",
        }}
      >
        <Button
          style={{
            color: "#0a8394",
            borderColor: "#0a8394",
            borderRadius: "4px",
            fontSize: "14px",
          }}
          onClick={handleAddRow}
          icon={<PlusOutlined />}
        >
          Add New
        </Button>
        <BulkUploadWidget onSuccess={fetchAutoSsrData} />
        <Button
          id="autoSsrSaveBtn"
          type="primary"
          onClick={handleSaveClick}
          loading={isSaving}
          style={{
            display: "none",
            backgroundColor: '#0A8394',
            borderColor: '#0A8394',
          }}
        >
          Save
        </Button>
        <Input
          placeholder="Search"
          value={globalSearch}
          onChange={(e: any) => {
            const value = e.target.value;
            setGlobalSearch(value);
          }}
          allowClear
          prefix={<SearchOutlined style={{ color: '#72747d' }} />}
          style={{ width: '300px' }}
        />
      </div>
      <div className="TableContainer" style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        {contextHolder}
        {isLoading ? (
          <Loader />
        ) : tableData.length === 0 ? (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              height: "100%",
              justifyContent: "center",
            }}
          >
            <Empty />
          </div>
        ) : (
          <AgTableClient
            // @ts-ignore
            rowData={tableData || []}
            columnDefs={columnDefs || []}
            footer={false}
            ref={gridRef}
            groupPanel
            showStatusBar
            autoResize
            singleClickEdit={true}
            enableDirectEdit
            onCellValueChanged={handleCellValueChanged}
          />
        )}
      </div>
    </div>
  );
}

